/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import _ from 'lodash';

import BottomBar from '../BottomBar';
import MultiSelectCheckbox from '../form/MultiSelectCheckbox';
import Checkbox from '../form/Checkbox';
import Input from '../form/Input';
import usePersistedDraft from '../hooks/usePersistedDraft';
import { updatePracticePreferences } from '../../../actions/preferences';
import { toMultiSelectOptions } from '../helpers';

const PracticeDetails5 = ({
  openLoadingPanel,
  closeLoadingPanel,
  nextPage,
  errorCallback,
  currentSubStep,
  previousPage,
  draftValues,
  onDraftChange,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  // Redux selectors
  const genderIdentityOptions = useSelector(state =>
    toMultiSelectOptions(
      _.get(state.misc, 'genderIdentities', []).filter(i => !i.other_yn),
    ),
  );
  const raceOptions = useSelector(state =>
    toMultiSelectOptions(
      _.get(state.misc, 'races', []).filter(r => !r.other_yn),
    ),
  );

  const { control, handleSubmit, watch, getValues } = useForm({
    defaultValues: draftValues || {
      gender_identities: [],
      gender_identity_other_yn: [false],
      gender_identity_other_name: '',
      display_gender_identity_yn: [false],
      with_disability_yn: [false],
      display_with_disability_yn: [false],
      neurodivergent_yn: [false],
      display_neurodivergent_yn: [false],
      lgbtq2sp_yn: [false],
      display_lgbtq2sp_yn: [false],
      races: [],
      race_other_yn: [false],
      race_other_name: '',
      display_race_yn: [false],
    },
  });

  usePersistedDraft(getValues, onDraftChange);

  const isGenderIdentityOtherVisible = watch(
    'gender_identity_other_yn',
  ).includes(true);
  const isRaceOtherVisible = watch('race_other_yn').includes(true);

  const onSubmit = async values => {
    const params = {
      gender_identity: {
        gender_identities: _.get(values, 'gender_identities', []).map(id => ({
          id,
        })),
        gender_identity_other_yn: _.get(
          values,
          'gender_identity_other_yn[0]',
          false,
        ),
        gender_identity_other_name: _.get(
          values,
          'gender_identity_other_name',
          '',
        ),
      },
      race: {
        races: _.get(values, 'races', []).map(id => ({ id })),
        race_other_yn: _.get(values, 'race_other_yn[0]', false),
        race_other_name: _.get(values, 'race_other_name', ''),
      },
      provider_self_identification: {
        display_gender_identity_yn: _.get(
          values,
          'display_gender_identity_yn[0]',
          false,
        ),
        with_disability_yn: _.get(values, 'with_disability_yn[0]', false),
        display_with_disability_yn: _.get(
          values,
          'display_with_disability_yn[0]',
          false,
        ),
        neurodivergent_yn: _.get(values, 'neurodivergent_yn[0]', false),
        display_neurodivergent_yn: _.get(
          values,
          'display_neurodivergent_yn[0]',
          false,
        ),
        lgbtq2sp_yn: _.get(values, 'lgbtq2sp_yn[0]', false),
        display_lgbtq2sp_yn: _.get(values, 'display_lgbtq2sp_yn[0]', false),
        display_race_yn: _.get(values, 'display_race_yn[0]', false),
      },
    };

    const errorMessage = intl.formatMessage({
      defaultMessage: 'There was a problem updating your practice details.',
    });

    openLoadingPanel(
      intl.formatMessage({ defaultMessage: 'Updating practice details...' }),
    );

    try {
      await dispatch(updatePracticePreferences(params));
      closeLoadingPanel();
      nextPage();
    } catch (err) {
      errorCallback(err, errorMessage);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="page-title">
        <h1>{intl.formatMessage({ defaultMessage: 'Practice Details' })}</h1>
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        <div className="sub-page-indicator">{`${currentSubStep + 1}/7`}</div>
      </div>

      <h3>{intl.formatMessage({ defaultMessage: 'Gender Identity' })}</h3>
      <div className="input-row">
        <Controller
          name="gender_identities"
          control={control}
          render={({ field }) => (
            <MultiSelectCheckbox {...field} options={genderIdentityOptions} />
          )}
        />
      </div>

      <div className="input-row" style={{ marginTop: -75 }}>
        <Controller
          name="gender_identity_other_yn"
          control={control}
          render={({ field }) => (
            <Checkbox
              {...field}
              options={[
                {
                  value: true,
                  label: intl.formatMessage({ defaultMessage: 'Other' }),
                },
              ]}
            />
          )}
        />
        {isGenderIdentityOtherVisible && (
          <Controller
            name="gender_identity_other_name"
            control={control}
            render={({ field }) => (
              // eslint-disable-next-line react/jsx-no-literals, formatjs/no-literal-string-in-jsx
              <Input {...field} label={<span>&zwnj;</span>} />
            )}
          />
        )}
      </div>

      <div className="input-row" style={{ marginTop: -47, marginBottom: 30 }}>
        <Controller
          name="display_gender_identity_yn"
          control={control}
          render={({ field }) => (
            <Checkbox
              {...field}
              options={[
                {
                  value: true,
                  label: intl.formatMessage({
                    defaultMessage: 'Display on profile',
                  }),
                },
              ]}
            />
          )}
        />
      </div>

      {/* Repeat for Disability, Neurodivergent, LGBTQ2S+ sections */}
      <h3>
        {intl.formatMessage({
          defaultMessage: 'Person Living with a Disability',
        })}
      </h3>
      <div className="input-row">
        <Controller
          name="with_disability_yn"
          control={control}
          render={({ field }) => (
            <Checkbox
              {...field}
              options={[
                {
                  value: true,
                  label: intl.formatMessage({
                    defaultMessage:
                      'I identify as a person living with a disability',
                  }),
                },
              ]}
            />
          )}
        />
      </div>
      <div className="input-row" style={{ marginTop: -47, marginBottom: 30 }}>
        <Controller
          name="display_with_disability_yn"
          control={control}
          render={({ field }) => (
            <Checkbox
              {...field}
              options={[
                {
                  value: true,
                  label: intl.formatMessage({
                    defaultMessage: 'Display on profile',
                  }),
                },
              ]}
            />
          )}
        />
      </div>

      <h3>{intl.formatMessage({ defaultMessage: 'Neurodivergent Person' })}</h3>
      <div className="input-row">
        <Controller
          name="neurodivergent_yn"
          control={control}
          render={({ field }) => (
            <Checkbox
              {...field}
              options={[
                {
                  value: true,
                  label: intl.formatMessage({
                    defaultMessage: 'I identify as a neurodivergent person',
                  }),
                },
              ]}
            />
          )}
        />
      </div>
      <div className="input-row" style={{ marginTop: -47, marginBottom: 30 }}>
        <Controller
          name="display_neurodivergent_yn"
          control={control}
          render={({ field }) => (
            <Checkbox
              {...field}
              options={[
                {
                  value: true,
                  label: intl.formatMessage({
                    defaultMessage: 'Display on profile',
                  }),
                },
              ]}
            />
          )}
        />
      </div>

      <h3>{intl.formatMessage({ defaultMessage: 'LGBTQ2S+' })}</h3>
      <div className="input-row">
        <Controller
          name="lgbtq2sp_yn"
          control={control}
          render={({ field }) => (
            <Checkbox
              {...field}
              options={[
                {
                  value: true,
                  label: intl.formatMessage({
                    defaultMessage: 'I identify as LGBTQ2S+',
                  }),
                },
              ]}
            />
          )}
        />
      </div>
      <div className="input-row" style={{ marginTop: -47, marginBottom: 30 }}>
        <Controller
          name="display_lgbtq2sp_yn"
          control={control}
          render={({ field }) => (
            <Checkbox
              {...field}
              options={[
                {
                  value: true,
                  label: intl.formatMessage({
                    defaultMessage: 'Display on profile',
                  }),
                },
              ]}
            />
          )}
        />
      </div>

      <h3>
        {intl.formatMessage({ defaultMessage: 'Racial/Ethnic Identity' })}
      </h3>
      <div className="input-row">
        <Controller
          name="races"
          control={control}
          render={({ field }) => (
            <MultiSelectCheckbox {...field} options={raceOptions} />
          )}
        />
      </div>

      <div className="input-row" style={{ marginTop: -75 }}>
        <Controller
          name="race_other_yn"
          control={control}
          render={({ field }) => (
            <Checkbox
              {...field}
              options={[
                {
                  value: true,
                  label: intl.formatMessage({ defaultMessage: 'Other' }),
                },
              ]}
            />
          )}
        />
        {isRaceOtherVisible && (
          <Controller
            name="race_other_name"
            control={control}
            render={({ field }) => (
              // eslint-disable-next-line react/jsx-no-literals, formatjs/no-literal-string-in-jsx
              <Input {...field} label={<span>&zwnj;</span>} />
            )}
          />
        )}
      </div>

      <div className="input-row" style={{ marginTop: -47 }}>
        <Controller
          name="display_race_yn"
          control={control}
          render={({ field }) => (
            <Checkbox
              {...field}
              options={[
                {
                  value: true,
                  label: intl.formatMessage({
                    defaultMessage: 'Display on profile',
                  }),
                },
              ]}
            />
          )}
        />
      </div>

      <BottomBar
        openLoadingPanel={openLoadingPanel}
        previousPage={previousPage}
        closeLoadingPanel={closeLoadingPanel}
        nextPage={nextPage}
        errorCallback={errorCallback}
        currentSubStep={currentSubStep}
      />
    </form>
  );
};

PracticeDetails5.propTypes = {
  openLoadingPanel: PropTypes.func.isRequired,
  closeLoadingPanel: PropTypes.func.isRequired,
  nextPage: PropTypes.func.isRequired,
  errorCallback: PropTypes.func.isRequired,
  currentSubStep: PropTypes.number.isRequired,
  previousPage: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  draftValues: PropTypes.object,
  onDraftChange: PropTypes.func,
};

export default PracticeDetails5;
